import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

type Step = {
  lead: string;
  body: string;
};

const STEPS: Step[] = [
  {
    lead: "Start the call:",
    body:
      "Click the phone button in the bottom-right corner of the page and " +
      "accept the terms and conditions. The call will then begin " +
      "automatically.",
  },
  {
    lead: "Minimize the window:",
    body:
      "Click the black chevron button at the bottom of the consultation " +
      "window to minimize it.",
  },
  {
    lead: "Navigate between sections:",
    body:
      "To move forward or go back, simply ask the agent to proceed to the " +
      "next section or return to the previous section. There are no " +
      "navigation buttons, allowing the consultation to flow naturally " +
      "through the conversation.",
  },
];

/**
 * "How to Use the Digital Consultation" — onboarding panel shown once, on
 * Section 1's first sub-step (the clock), right after the user picks
 * "AI Guided" on the splash. Explains how to start the ElevenLabs call,
 * minimise its window and move between sections by talking to the agent.
 *
 * Styled to stand out from the neutral analysis card (primary-tinted
 * gradient, primary border, a centred bold "Read before you start" heading,
 * bold leads) so the user actually reads it before pressing the call button.
 * Kept compact on purpose: it has to fit in the ~170px the analysis card has
 * free below the clock (the slot the progress timeline later takes) without
 * growing the card or making the page scroll. Fades and slides in on mount;
 * it is unmounted as soon as the user advances to the next sub-step (see
 * `BioAgeSection`).
 */
const ConsultationHowToCard: React.FC = () => {
  const theme: Theme = useTheme();

  return (
    <Box
      component="aside"
      aria-label="How to use the digital consultation"
      sx={styles.card(theme)}
    >
      <Box sx={styles.header}>
        <Box sx={styles.title(theme)}>Read before you start</Box>
      </Box>

      <Box component="ul" sx={styles.list}>
        {STEPS.map((step) => (
          <Box component="li" key={step.lead} sx={styles.text(theme)}>
            <Box component="span" sx={styles.lead(theme)}>
              {step.lead}
            </Box>{" "}
            {step.body}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ConsultationHowToCard;

const styles = {
  card: (theme: Theme) => ({
    width: "100%",
    boxSizing: "border-box" as const,
    padding: "8px 14px",
    borderRadius: "12px",
    // Primary-tinted so it reads as a call-out against the white analysis
    // card. Same light-blue → white gradient as the dashboard's
    // StartConsultationCard; the border is one step darker on the primary
    // scale so the edge is visible on the gradient.
    border: `1px solid ${theme.colorTokens.P[20]}`,
    backgroundImage: `linear-gradient(110.41deg, ${theme.colorTokens.P[10]}B4 42.102%, ${theme.colors_ds.NEUTRAL_BG_MAIN} 95.178%)`,
    boxShadow:
      "0px 2px 8px rgba(0, 0, 0, 0.04), 0px 8px 20px rgba(0, 0, 0, 0.06)",
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
    "@keyframes consultationHowToIn": {
      from: { opacity: 0, transform: "translateY(8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    animation: "consultationHowToIn 400ms ease both",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
  },
  title: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  list: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  text: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.45,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  lead: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
};
